import React, { useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import { BiDownload } from 'react-icons/bi';
import { Loader2, X } from 'src/components/common/icons';
import { Field, SelectField } from './Master';

interface IDraftsDueReportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    format: DraftsDueReportFormat;
}

type DraftsDueReportType = 'drafts-due' | 'by-client' | 'historical' | 'historical-recording-fees';
type DraftsDueReportFormat = 'EXCELOPENXML' | 'PDF';

const REPORT_OPTIONS = [
    { value: 'drafts-due', label: 'By Date' },
    { value: 'by-client', label: 'By Client' },
    { value: 'historical', label: 'By Client (Historical)' },
    { value: 'historical-recording-fees', label: 'By Client (Historical with Recording Fees)' },
];

const CHECK_STATUS_OPTIONS = [
    { value: 'A', label: 'All' },
    { value: 'P', label: 'Paid' },
    { value: 'N', label: 'Not Paid' },
];

const TITLE_STATUS_OPTIONS = [
    { value: 'A', label: 'Any' },
    { value: 'Y', label: 'Good' },
    { value: 'N', label: 'Bad' },
];

const FUNDED_OPTIONS = [
    { value: 'A', label: 'All' },
    { value: 'N', label: 'Not Funded' },
    { value: 'Y', label: 'Funded' },
];

const SUPPRESS_OPTIONS = [
    { value: 'Y', label: 'Yes' },
    { value: 'N', label: 'No' },
];

const getToday = (): string => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
};

const formatDateToMMDDYYYY = (value: string): string => {
    if (!value) {
        return '';
    }

    const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : value;
};

const getReportFileName = (reportType: DraftsDueReportType, format: DraftsDueReportFormat): string => {
    const names: Record<DraftsDueReportType, string> = {
        'drafts-due': 'DraftsDue',
        'by-client': 'DraftsDueByClient',
        historical: 'DraftsDueHistorical',
        'historical-recording-fees': 'DraftsDueHistoricalWithRecordingFees',
    };

    const extension = format === 'PDF' ? 'pdf' : 'xlsx';
    return `${names[reportType]}_${getToday().replace(/-/g, '')}.${extension}`;
};

const DraftsDueReportDialog: React.FC<IDraftsDueReportDialogProps> = ({ isOpen, onClose, format }) => {
    const { get, response } = useFetch();
    const [reportType, setReportType] = useState<DraftsDueReportType>('drafts-due');
    const [clientNo, setClientNo] = useState('');
    const [projectNo, setProjectNo] = useState('');
    const [beginningDate, setBeginningDate] = useState('');
    const [endingDate, setEndingDate] = useState(getToday);
    const [checkStatus, setCheckStatus] = useState('N');
    const [titleStatus, setTitleStatus] = useState('A');
    const [funded, setFunded] = useState('A');
    const [suppress, setSuppress] = useState('N');
    const [reportDate, setReportDate] = useState(getToday);
    const [generatingFormat, setGeneratingFormat] = useState<DraftsDueReportFormat | null>(null);
    const [message, setMessage] = useState('');
    const isGenerating = generatingFormat !== null;

    const isHistorical = useMemo(() => reportType === 'historical' || reportType === 'historical-recording-fees', [reportType]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        setMessage('');
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !isGenerating) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isGenerating, isOpen, onClose]);

    const handleDownload = async (): Promise<void> => {
        if (beginningDate && endingDate && beginningDate > endingDate) {
            setMessage('Beginning Date cannot be after Ending Date.');
            return;
        }
        if (isHistorical && !reportDate) {
            setMessage('Report Date is required for historical reports.');
            return;
        }

        setGeneratingFormat(format);
        setMessage('');

        try {
            const params = new URLSearchParams();
            params.append('reportType', reportType);
            params.append('clientNo', clientNo.trim());
            params.append('projectNo', projectNo.trim());
            params.append('beginningDate', formatDateToMMDDYYYY(beginningDate));
            params.append('endingDate', formatDateToMMDDYYYY(endingDate));
            params.append('checkStatus', checkStatus);
            params.append('titleStatus', titleStatus);
            params.append('funded', funded);
            params.append('suppress', suppress);
            params.append('reportDate', isHistorical ? formatDateToMMDDYYYY(reportDate) : '');
            params.append('format', format);

            await get(`/v1.0/leases/admin/drafts-due/report?${params.toString()}`);
            if (!response.ok) {
                setMessage('The Drafts Due report could not be generated.');
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = getReportFileName(reportType, format);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch {
            setMessage('The Drafts Due report could not be generated.');
        } finally {
            setGeneratingFormat(null);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={dialogStyles.overlay} onClick={isGenerating ? undefined : onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="drafts-due-dialog-title"
                style={dialogStyles.content}
                onClick={(event) => event.stopPropagation()}
            >
                <div style={dialogStyles.header}>
                    <h2 id="drafts-due-dialog-title" style={dialogStyles.title}>
                        Drafts Due Reports
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        style={dialogStyles.closeButton}
                        aria-label="Close Drafts Due Reports"
                        disabled={isGenerating}
                    >
                        <X style={{ width: '1.1rem', height: '1.1rem' }} />
                    </button>
                </div>

                <div style={dialogStyles.body}>
                    <div style={dialogStyles.fieldsGrid}>
                        <div style={dialogStyles.fullWidthField}>
                            <SelectField
                                label="Sort By"
                                value={reportType}
                                editable={true}
                                options={REPORT_OPTIONS}
                                onChange={(value) => setReportType((value || 'drafts-due') as DraftsDueReportType)}
                            />
                        </div>
                        <Field label="Client Number" type="number" value={clientNo} editable={true} onChange={(value) => setClientNo(value || '')} />
                        <Field
                            label="Project Number"
                            type="number"
                            value={projectNo}
                            editable={true}
                            onChange={(value) => setProjectNo(value || '')}
                        />
                        <Field
                            label="Beginning Date"
                            type="date"
                            value={beginningDate}
                            editable={true}
                            onChange={(value) => setBeginningDate(value || '')}
                        />
                        <Field label="Ending Date" type="date" value={endingDate} editable={true} onChange={(value) => setEndingDate(value || '')} />
                        <SelectField
                            label="Check Status?"
                            value={checkStatus}
                            editable={true}
                            options={CHECK_STATUS_OPTIONS}
                            onChange={(value) => setCheckStatus(value || 'A')}
                        />
                        <SelectField
                            label="Title Status?"
                            value={titleStatus}
                            editable={true}
                            options={TITLE_STATUS_OPTIONS}
                            onChange={(value) => setTitleStatus(value || 'A')}
                        />
                        <SelectField
                            label="Funded?"
                            value={funded}
                            editable={true}
                            options={FUNDED_OPTIONS}
                            onChange={(value) => setFunded(value || 'A')}
                        />
                        <SelectField
                            label="Suppress?"
                            value={suppress}
                            editable={true}
                            options={SUPPRESS_OPTIONS}
                            onChange={(value) => setSuppress(value || 'N')}
                        />
                        {isHistorical && (
                            <Field
                                label="Report Date"
                                type="date"
                                value={reportDate}
                                editable={true}
                                onChange={(value) => setReportDate(value || '')}
                            />
                        )}
                    </div>

                    {message && (
                        <div role="alert" style={dialogStyles.message}>
                            {message}
                        </div>
                    )}
                </div>

                <div style={dialogStyles.footer}>
                    <button type="button" onClick={onClose} style={dialogStyles.secondaryButton} disabled={isGenerating}>
                        Cancel
                    </button>
                    <button type="button" onClick={handleDownload} style={dialogStyles.primaryButton} disabled={isGenerating}>
                        <span style={dialogStyles.buttonInner}>
                            {isGenerating ? (
                                <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />
                            ) : (
                                <BiDownload aria-hidden="true" style={{ width: '1rem', height: '1rem' }} />
                            )}
                            {isGenerating ? 'Generating...' : `Download (.${format === 'PDF' ? 'pdf' : 'xlsx'})`}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.58)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
    },
    content: {
        width: 'min(720px, 96vw)',
        maxHeight: '92vh',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 24px 56px rgba(15, 23, 42, 0.28)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        minHeight: '58px',
        padding: '0.85rem 1rem',
        borderBottom: '1px solid #E2E8F0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
    },
    title: {
        margin: 0,
        color: '#0F172A',
        fontSize: '1.05rem',
        fontWeight: 700,
    },
    closeButton: {
        width: '2rem',
        height: '2rem',
        padding: 0,
        border: 'none',
        borderRadius: '6px',
        backgroundColor: 'transparent',
        color: '#64748B',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        padding: '1rem',
        overflowY: 'auto',
    },
    fieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0.8rem 1rem',
        alignItems: 'end',
    },
    fullWidthField: {
        gridColumn: '1 / -1',
    },
    message: {
        marginTop: '0.9rem',
        padding: '0.65rem 0.75rem',
        border: '1px solid #FCA5A5',
        borderRadius: '6px',
        backgroundColor: '#FEF2F2',
        color: '#991B1B',
        fontSize: '0.8rem',
    },
    footer: {
        padding: '0.8rem 1rem',
        borderTop: '1px solid #E2E8F0',
        backgroundColor: '#F8FAFC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.65rem',
    },
    buttonInner: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
    },
    primaryButton: {
        minHeight: '34px',
        padding: '0.45rem 0.85rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#1E40AF',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        minHeight: '34px',
        padding: '0.45rem 0.85rem',
        border: '1px solid #CBD5E1',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#0F172A',
        cursor: 'pointer',
        fontWeight: 600,
    },
};

export default DraftsDueReportDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/DraftsDueReportDialog.tsx